import { useEffect, useRef } from 'react';
import { animate, createScope, stagger } from 'animejs';

/**
 * useAnimePageLoad — world-class page-load entrance animations using anime.js v4.
 *
 * Animates elements with [data-anime-enter] on mount with staggered timing.
 * Creates a premium, lively entrance that makes the page feel alive.
 *
 * @param {object} opts - Configuration options
 * @param {string} opts.selector - CSS selector for entrance elements (default: '[data-anime-enter]')
 * @param {number} opts.baseDelay - Base delay before animation starts (default: 100)
 * @param {number} opts.stagger - Stagger between elements (default: 120)
 * @param {number} opts.duration - Animation duration (default: 900)
 */
function useAnimePageLoad({
  selector = '[data-anime-enter]',
  baseDelay = 100,
  stagger: staggerVal = 120,
  duration = 900,
} = {}) {
  const rootRef = useRef(null);
  const scopeRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current) return undefined;

    scopeRef.current = createScope({ root: rootRef.current }).add(() => {
      const enterItems = rootRef.current.querySelectorAll(selector);
      if (!enterItems.length) return;

      enterItems.forEach((el) => {
        el.style.opacity = '1';
      });

      animate(Array.from(enterItems), {
        opacity: [0.9, 1],
        y: [18, 0],
        duration,
        delay: stagger(staggerVal, { start: baseDelay }),
        ease: 'out(3)',
      });
    });

    return () => scopeRef.current?.revert();
  }, [selector, baseDelay, staggerVal, duration]);

  return rootRef;
}

export default useAnimePageLoad;
